/**
 * Template Editor - Full Page Experience
 * 
 * A proper, large-surface email editor that replaces the cramped modal.
 * 
 * Features:
 * - Full page layout with generous editor space
 * - Edit/Preview toggle with comfortable viewing
 * - Collapsible token helper sidebar
 * - Sticky header with actions
 * - Dark mode optimized
 * 
 * SAFETY:
 * - Uses existing template API endpoints
 * - No schema changes
 * - No sending behavior changes
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  FloppyDisk,
  Eye,
  PencilSimple,
  TextBolder,
  TextItalic,
  TextUnderline,
  ListBullets,
  ListNumbers,
  Link as LinkIcon,
  TextH,
  Info,
  CaretRight,
  CaretDown,
  X,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Auth fetch helper
const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
};

// Sanitize HTML for safe output and normalize presentation styles
const sanitizeHtml = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  
  // Remove dangerous tags
  const dangerous = div.querySelectorAll('script, style, iframe, form, object, embed');
  dangerous.forEach(el => el.remove());
  
  // List of inline styles to strip (presentation-only, break dark theme)
  const stylesToRemove = ['color', 'background-color', 'background', 'font-family', 'font-size'];
  
  // Process all elements
  const all = div.querySelectorAll('*');
  all.forEach(el => {
    // Remove event handlers and javascript hrefs
    [...el.attributes].forEach(attr => {
      if (attr.name.startsWith('on') || (attr.name === 'href' && attr.value.startsWith('javascript:'))) {
        el.removeAttribute(attr.name);
      }
    });
    
    // Strip presentation-only inline styles while preserving others
    if (el.style && el.style.length > 0) {
      stylesToRemove.forEach(prop => {
        el.style.removeProperty(prop);
      });
      // Remove empty style attribute
      if (el.getAttribute('style') === '' || !el.style.length) {
        el.removeAttribute('style');
      }
    }
  });
  
  return div.innerHTML;
};

// ============================================================================
// TEMPLATE EDITOR COMPONENT
// ============================================================================

export default function TemplateEditor() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !templateId;
  const returnTab = searchParams.get('tab') || 'templates';

  // Form state
  const [form, setForm] = useState({
    name: "",
    subject: "",
    html_body: "",
    template_type: "shared",
    description: "",
  });
  const [originalForm, setOriginalForm] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("edit"); // "edit" or "preview"
  const [showTokens, setShowTokens] = useState(false);
  const [allowedTokens, setAllowedTokens] = useState({});
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Editor ref
  const editorRef = useRef(null);
  const isFormattingRef = useRef(false);

  // Load template data and tokens
  useEffect(() => {
    fetchAllowedTokens();
    if (templateId) {
      fetchTemplate();
    }
  }, [templateId]);

  // Sync editor with form state
  useEffect(() => {
    if (mode === "edit" && editorRef.current && !isFormattingRef.current) {
      if (editorRef.current.innerHTML !== form.html_body) {
        editorRef.current.innerHTML = form.html_body;
      }
    }
  }, [mode, form.html_body]);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/templates/${templateId}`);
      if (res.ok) {
        const data = await res.json();
        const tmpl = data.template;
        // Normalize existing content by stripping presentation styles on load
        const normalizedBody = tmpl.html_body ? sanitizeHtml(tmpl.html_body) : "";
        const formData = {
          name: tmpl.name || "",
          subject: tmpl.subject || "",
          html_body: normalizedBody,
          template_type: tmpl.template_type || "shared",
          description: tmpl.description || "",
        };
        setForm(formData);
        setOriginalForm(formData);
      } else {
        toast.error("Failed to load template");
        navigate(`/admin/tenant-communications?tab=${returnTab}`);
      }
    } catch (err) {
      console.error("Failed to fetch template:", err);
      toast.error("Failed to load template");
      navigate(`/admin/tenant-communications?tab=${returnTab}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllowedTokens = async () => {
    try {
      const res = await authFetch(`${API}/admin/tenant-comms/templates/tokens`);
      if (res.ok) {
        const data = await res.json();
        setAllowedTokens(data.tokens || {});
      }
    } catch (err) {
      console.error("Failed to fetch tokens:", err);
    }
  };

  // Rich text commands
  const execCommand = useCallback((command, value = null) => {
    isFormattingRef.current = true;
    document.execCommand(command, false, value);
    if (editorRef.current) {
      const sanitized = sanitizeHtml(editorRef.current.innerHTML);
      setForm(prev => ({ ...prev, html_body: sanitized }));
    }
    setTimeout(() => {
      isFormattingRef.current = false;
    }, 100);
  }, []);

  const handleInsertLink = useCallback(() => {
    const url = prompt("Enter URL:");
    if (url) {
      execCommand("createLink", url);
    }
  }, [execCommand]);

  const handleEditorInput = useCallback(() => {
    if (editorRef.current && !isFormattingRef.current) {
      const sanitized = sanitizeHtml(editorRef.current.innerHTML);
      setForm(prev => ({ ...prev, html_body: sanitized }));
    }
  }, []);

  // Handle paste - strip presentation styles immediately for clean pasting
  const handleEditorPaste = useCallback((e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    
    if (html) {
      // Sanitize pasted HTML to remove presentation styles
      const sanitized = sanitizeHtml(html);
      document.execCommand('insertHTML', false, sanitized);
    } else if (text) {
      document.execCommand('insertText', false, text);
    }
    
    // Sync state
    if (editorRef.current) {
      const finalContent = sanitizeHtml(editorRef.current.innerHTML);
      setForm(prev => ({ ...prev, html_body: finalContent }));
    }
  }, []);

  // Insert token
  const insertToken = useCallback((token) => {
    const tokenStr = `{{${token}}}`;
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand("insertText", false, tokenStr);
      handleEditorInput();
    }
  }, [handleEditorInput]);

  // Load preview
  const loadPreview = async () => {
    if (!form.html_body.trim()) {
      setPreviewData(null);
      return;
    }

    setLoadingPreview(true);
    try {
      let htmlContent = form.html_body;
      if (editorRef.current) {
        htmlContent = sanitizeHtml(editorRef.current.innerHTML);
      }

      const res = await authFetch(
        `${API}/admin/tenant-comms/templates/preview-inline?subject=${encodeURIComponent(form.subject)}&html_body=${encodeURIComponent(htmlContent)}`,
        { method: "POST" }
      );

      if (res.ok) {
        const data = await res.json();
        setPreviewData(data);
      }
    } catch (err) {
      console.error("Failed to load preview:", err);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Toggle mode and load preview
  const toggleMode = async (newMode) => {
    if (newMode === "preview" && mode === "edit") {
      // Capture editor content before switching
      if (editorRef.current) {
        const sanitized = sanitizeHtml(editorRef.current.innerHTML);
        setForm(prev => ({ ...prev, html_body: sanitized }));
      }
      await loadPreview();
    }
    setMode(newMode);
  };

  // Save template
  const saveTemplate = async () => {
    if (!form.name.trim()) {
      toast.error("Template name is required");
      return;
    }
    if (!form.subject.trim()) {
      toast.error("Email subject is required");
      return;
    }

    let finalHtml = form.html_body;
    if (editorRef.current) {
      finalHtml = sanitizeHtml(editorRef.current.innerHTML);
    }

    if (!finalHtml.trim() || finalHtml === '<br>' || finalHtml === '<div><br></div>') {
      toast.error("Email body is required");
      return;
    }

    setSaving(true);
    try {
      const url = templateId
        ? `${API}/admin/tenant-comms/templates/${templateId}`
        : `${API}/admin/tenant-comms/templates`;

      const res = await authFetch(url, {
        method: templateId ? "PATCH" : "POST",
        body: JSON.stringify({
          ...form,
          html_body: finalHtml,
        }),
      });

      if (res.ok) {
        toast.success(templateId ? "Template saved" : "Template created");
        navigate(`/admin/tenant-communications?tab=${returnTab}`);
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || "Failed to save template");
      }
    } catch (err) {
      console.error("Failed to save template:", err);
      toast.error("Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  // Check for unsaved changes
  const hasChanges = () => {
    if (!originalForm) return form.name || form.subject || form.html_body;
    return JSON.stringify(form) !== JSON.stringify(originalForm);
  };

  const handleBack = () => {
    if (hasChanges()) {
      if (window.confirm("You have unsaved changes. Are you sure you want to leave?")) {
        navigate(`/admin/tenant-communications?tab=${returnTab}`);
      }
    } else {
      navigate(`/admin/tenant-communications?tab=${returnTab}`);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-zinc-400">Loading template...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col">
      {/* ============================================
          STICKY HEADER
          ============================================ */}
      <header className="sticky top-0 z-50 bg-zinc-900 border-b border-zinc-800 px-6 py-4">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-4">
          {/* Left: Back + Title */}
          <div className="flex items-center gap-4 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 flex-shrink-0"
            >
              <ArrowLeft size={20} className="mr-2" />
              Back
            </Button>
            
            <div className="h-6 w-px bg-zinc-700 flex-shrink-0" />
            
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-zinc-100 truncate">
                {isNew ? "New Template" : form.name || "Edit Template"}
              </h1>
              {!isNew && (
                <p className="text-sm text-zinc-500">
                  {form.template_type === "shared" ? "Shared Template" : 
                   form.template_type === "campaign" ? "Campaign Template" : "Automation Template"}
                </p>
              )}
            </div>
          </div>

          {/* Center: Edit/Preview Toggle */}
          <div className="flex items-center gap-1 bg-zinc-800 rounded-lg p-1">
            <button
              onClick={() => toggleMode("edit")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
                mode === "edit"
                  ? "bg-[#FF6600] text-white"
                  : "text-zinc-400 hover:text-zinc-100"
              }`}
            >
              <PencilSimple size={16} />
              Edit
            </button>
            <button
              onClick={() => toggleMode("preview")}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
                mode === "preview"
                  ? "bg-[#FF6600] text-white"
                  : "text-zinc-400 hover:text-zinc-100"
              }`}
            >
              <Eye size={16} />
              Preview
            </button>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <Button
              variant="outline"
              onClick={handleBack}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              onClick={saveTemplate}
              disabled={saving}
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white px-6"
            >
              <FloppyDisk size={18} className="mr-2" />
              {saving ? "Saving..." : "Save Template"}
            </Button>
          </div>
        </div>
      </header>

      {/* ============================================
          MAIN CONTENT AREA
          ============================================ */}
      <main className="flex-1 overflow-hidden">
        <div className="h-full max-w-[1600px] mx-auto flex">
          {/* ============================================
              EDITOR PANE (or Preview)
              ============================================ */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {mode === "edit" ? (
              <>
                {/* Template Meta - Compact Top Section */}
                <div className="bg-zinc-900/50 border-b border-zinc-800 px-8 py-5">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <div className="space-y-2">
                      <Label className="text-zinc-300 text-sm">Template Name *</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="e.g., Welcome Email"
                        className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 h-11"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300 text-sm">Type</Label>
                      <Select
                        value={form.template_type}
                        onValueChange={(val) => setForm({ ...form, template_type: val })}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100 h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          <SelectItem value="shared" className="text-zinc-100">Shared</SelectItem>
                          <SelectItem value="campaign" className="text-zinc-100">Campaign Only</SelectItem>
                          <SelectItem value="automation" className="text-zinc-100">Automation Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300 text-sm">Description (optional)</Label>
                      <Input
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Brief description"
                        className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 h-11"
                      />
                    </div>
                  </div>
                </div>

                {/* Subject Line - Full Width with Token Hint */}
                <div className="bg-zinc-900/30 border-b border-zinc-800 px-8 py-5">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-zinc-200 text-sm font-medium">Subject Line *</Label>
                      <span className="text-zinc-500 text-xs">{form.subject.length}/200</span>
                    </div>
                    <Input
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      placeholder="e.g., Welcome {{first_name}}!"
                      className="bg-zinc-800 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 h-12 text-base"
                    />
                    {/* Inline Token Hint */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-zinc-500 text-xs">Insert:</span>
                      {Object.keys(allowedTokens).slice(0, 5).map((token) => (
                        <button
                          key={token}
                          type="button"
                          onClick={() => setForm({ ...form, subject: form.subject + `{{${token}}}` })}
                          className="px-2 py-0.5 bg-zinc-800 hover:bg-zinc-700 rounded text-xs text-zinc-400 hover:text-zinc-200 font-mono transition-colors"
                        >
                          {`{{${token}}}`}
                        </button>
                      ))}
                      {Object.keys(allowedTokens).length > 5 && (
                        <button
                          type="button"
                          onClick={() => setShowTokens(true)}
                          className="text-[#FF6600] text-xs hover:underline"
                        >
                          +{Object.keys(allowedTokens).length - 5} more
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Rich Text Editor - Main Canvas */}
                <div className="flex-1 flex flex-col overflow-hidden bg-zinc-950">
                  {/* Toolbar */}
                  <div className="flex items-center justify-between px-8 py-3 bg-zinc-900 border-b border-zinc-800">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => execCommand("bold")}
                        className="p-2.5 rounded-lg hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors"
                        title="Bold"
                      >
                        <TextBolder size={20} weight="bold" />
                      </button>
                      <button
                        type="button"
                        onClick={() => execCommand("italic")}
                        className="p-2.5 rounded-lg hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors"
                        title="Italic"
                      >
                        <TextItalic size={20} />
                      </button>
                      <button
                        type="button"
                        onClick={() => execCommand("underline")}
                        className="p-2.5 rounded-lg hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors"
                        title="Underline"
                      >
                        <TextUnderline size={20} />
                      </button>
                      <div className="w-px h-6 bg-zinc-700 mx-2" />
                      <button
                        type="button"
                        onClick={() => execCommand("formatBlock", "h2")}
                        className="p-2.5 rounded-lg hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors"
                        title="Heading"
                      >
                        <TextH size={20} />
                      </button>
                      <button
                        type="button"
                        onClick={() => execCommand("insertUnorderedList")}
                        className="p-2.5 rounded-lg hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors"
                        title="Bullet List"
                      >
                        <ListBullets size={20} />
                      </button>
                      <button
                        type="button"
                        onClick={() => execCommand("insertOrderedList")}
                        className="p-2.5 rounded-lg hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors"
                        title="Numbered List"
                      >
                        <ListNumbers size={20} />
                      </button>
                      <div className="w-px h-6 bg-zinc-700 mx-2" />
                      <button
                        type="button"
                        onClick={handleInsertLink}
                        className="p-2.5 rounded-lg hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors"
                        title="Insert Link"
                      >
                        <LinkIcon size={20} />
                      </button>
                    </div>
                    
                    {/* Token Toggle */}
                    <div className="flex items-center gap-2">
                      {/* Quick token insert dropdown */}
                      <div className="flex items-center gap-1 bg-zinc-800/50 rounded-lg px-2 py-1">
                        <span className="text-zinc-500 text-xs">Insert:</span>
                        {Object.keys(allowedTokens).slice(0, 3).map((token) => (
                          <button
                            key={token}
                            type="button"
                            onClick={() => insertToken(token)}
                            className="px-2 py-1 bg-zinc-700 hover:bg-zinc-600 rounded text-xs text-zinc-300 hover:text-white font-mono transition-colors"
                          >
                            {`{{${token}}}`}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => setShowTokens(!showTokens)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          showTokens
                            ? "bg-[#FF6600] text-white"
                            : "bg-zinc-800 text-zinc-300 hover:text-white border border-zinc-700 hover:border-zinc-600"
                        }`}
                      >
                        <Info size={16} />
                        All Tokens
                        {showTokens ? <CaretDown size={14} /> : <CaretRight size={14} />}
                      </button>
                    </div>
                  </div>

                  {/* Editor Area */}
                  <div className="flex-1 overflow-y-auto px-8 py-8">
                    <div
                      ref={editorRef}
                      contentEditable
                      onInput={handleEditorInput}
                      onPaste={handleEditorPaste}
                      className="min-h-[500px] max-w-4xl mx-auto p-10 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#FF6600]/50 prose prose-invert prose-lg max-w-none [&_a]:text-[#FF6600] [&_h2]:text-2xl [&_h2]:font-semibold [&_h2]:mb-4 [&_p]:mb-4 [&_ul]:mb-4 [&_ol]:mb-4"
                      style={{ lineHeight: 1.8, fontSize: '16px' }}
                      data-placeholder="Start writing your email content here..."
                    />
                  </div>
                </div>
              </>
            ) : (
              /* ============================================
                 PREVIEW MODE
                 ============================================ */
              <div className="flex-1 overflow-y-auto bg-zinc-950 px-8 py-8">
                <div className="max-w-3xl mx-auto space-y-6">
                  {loadingPreview ? (
                    <div className="text-center py-20 text-zinc-400">
                      Loading preview...
                    </div>
                  ) : previewData ? (
                    <>
                      {/* Subject Preview */}
                      <div className="space-y-3">
                        <Label className="text-zinc-400 text-sm">Subject</Label>
                        <div className="bg-zinc-900 rounded-xl p-5 border border-zinc-800">
                          <p className="text-zinc-100 text-xl font-medium">
                            {previewData.rendered_subject}
                          </p>
                        </div>
                      </div>

                      {/* Email Body Preview */}
                      <div className="space-y-3">
                        <Label className="text-zinc-400 text-sm">Email Body</Label>
                        <div className="bg-white rounded-xl p-10 border border-zinc-300 shadow-lg">
                          <div
                            className="prose prose-lg max-w-none [&_a]:text-[#FF6600]"
                            dangerouslySetInnerHTML={{ __html: previewData.rendered_body }}
                          />
                        </div>
                      </div>

                      {/* Sample Data */}
                      <div className="bg-zinc-900/50 rounded-xl p-5 border border-zinc-800">
                        <Label className="text-zinc-400 text-xs mb-3 block">Sample Token Values</Label>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(previewData.sample_data_used || {}).map(([key, value]) => (
                            <span key={key} className="text-xs bg-zinc-800 px-3 py-1.5 rounded-lg text-zinc-400">
                              <span className="font-mono text-zinc-300">{`{{${key}}}`}</span>
                              <span className="mx-1.5">→</span>
                              <span className="text-zinc-200">{value}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-20 text-zinc-500">
                      <Eye size={48} className="mx-auto mb-4 opacity-30" />
                      <p>Add some content and switch to Preview to see how it looks</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ============================================
              TOKEN SIDEBAR (collapsible)
              ============================================ */}
          {showTokens && mode === "edit" && (
            <aside className="w-72 border-l border-zinc-800 bg-zinc-900/50 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
                <h3 className="text-sm font-medium text-zinc-200">Available Tokens</h3>
                <button
                  onClick={() => setShowTokens(false)}
                  className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-3">
                <p className="text-zinc-500 text-xs mb-4">
                  Click a token to insert it at the cursor position in your email.
                </p>
                {Object.entries(allowedTokens).map(([token, desc]) => (
                  <button
                    key={token}
                    onClick={() => insertToken(token)}
                    className="w-full text-left p-3 bg-zinc-800 hover:bg-zinc-700 rounded-lg transition-colors group"
                  >
                    <code className="text-[#FF6600] text-sm font-mono block mb-1">
                      {`{{${token}}}`}
                    </code>
                    <span className="text-zinc-500 text-xs group-hover:text-zinc-400">
                      {desc}
                    </span>
                  </button>
                ))}
              </div>
            </aside>
          )}
        </div>
      </main>
    </div>
  );
}
